/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, {useEffect, useState} from 'react';
import {FormCheck, InputGroup} from "react-bootstrap";
import PropTypes from 'prop-types';
import {findIndex, assign} from 'lodash';

import {useDispatch, useSelector} from "react-redux";
import {getManagedListMatchLogs, getManagedListMatchLogsCount, setCurrentPage, setOrder, setSelectedId} from "./managedListMatchLogsSlice";

import TextHoverDisplay from "../../common/components/TextHoverDisplay";
import TableWithPaging from '../../common/components/TableWithPaging';
import {DateTime} from "luxon";
import CheckBox from "../../common/components/CheckBox";

const ManagedListMatchLogsTable = (props) => {
    const dispatch = useDispatch();
    const managedListMatchLogsState = useSelector((state) => state.managedListMatchLogs);

    useEffect(() => {
        // When open dialog, we clear state.
        dispatch(getManagedListMatchLogsCount());
        dispatch(getManagedListMatchLogs());
    }, [props.lastReload, dispatch]);

    const createHeader = (row) => {
        return [
            {
                id: 1,
                name: '',
                property: 'id',
                sortable: false,
                formatted: true,
                formatBy: (row) => {
                    // let image = tbdBrainLightningIcon;
                    return <div>
                        <CheckBox
                            innerState
                            onChange={value =>
                                dispatch(setSelectedId({
                                    id: row.id,
                                    name: row.name,
                                    type: row.type,
                                    status: row.status,
                                    isChecked: value
                                }))
                            }
                        />
                    </div>
                }
            },
            {
                id: 2,
                name: 'Id',
                property: 'id',
                sortable: false,
                formatted: true,
                formatBy: (row) => {
                    if (row.id) {
                        return <TextHoverDisplay
                            truncatedText={row.id.substring(0, 13)}
                            fullText={row.id}
                        />
                    }
                    return <span></span>
                }
            },
            {
                id: 3,
                name: 'Organization Id',
                property: 'organizationId',
                sortable: false,
                formatted: true,
                formatBy: (row) => {
                    if (row.id) {
                        return <TextHoverDisplay
                            truncatedText={row.organizationId.substring(0, 13)}
                            fullText={row.organizationId}
                        />
                    }
                    return <span></span>
                }
            },
            {
                id: 4,
                name: 'List Id',
                property: 'listId',
                sortable: false,
                formatted: true,
                formatBy: (row) => {
                    if (row.id) {
                        return <TextHoverDisplay
                            truncatedText={row.listId.substring(0, 13)}
                            fullText={row.listId}
                        />
                    }
                    return <span></span>
                }
            },
            {
                id: 5,
                name: 'List Match Id',
                property: 'listMatchId',
                sortable: false,
                formatted: true,
                formatBy: (row) => {
                    if (row.id) {
                        return <TextHoverDisplay
                            truncatedText={row.listMatchId.substring(0, 13)}
                            fullText={row.listMatchId}
                        />
                    }
                    return <span></span>
                }
            },
            {
                id: 6,
                name: 'Level',
                property: 'level',
                sortable: true,
                formatted: false
            },
            {
                id: 7,
                name: 'Message',
                property: 'message',
                sortable: false,
                formatted: false,
            },
            {
                id: 8,
                name: 'Created',
                property: 'created',
                sortable: true,
                formatted: true,
                formatBy: (row, header) => {
                    return DateTime.fromISO(row.created).toLocaleString(DateTime.DATETIME_MED);
                }
            },
        ];
    };

    const headersUtil = (hds, hd, sortOrder) => {
        const _headers = hds.map(hc => assign(hc, {sortOrder: null}));
        _headers.splice(
            findIndex(hds, {id: hd.id}),
            1,
            assign({}, hd, {
                sortOrder: sortOrder,
            })
        );
        return _headers;
    };

    const handleSort = (header) => {
        if (!header.sortable) return;
        let _sortOrder;
        if (!header.sortOrder) _sortOrder = 'ASC';
        if (header.sortOrder === 'ASC') _sortOrder = 'DESC';
        if (header.sortOrder === 'DESC') _sortOrder = 'ASC';
        setHeader(headersUtil(headers, header, _sortOrder));
        dispatch(setOrder({by: header.property, direction: _sortOrder}));
        onSelectPage(
            1,
            {
                [header.property]: _sortOrder.toLowerCase()
            }
        );
    };

    const onSelectPage = (page) => {
        dispatch(setCurrentPage(page));
        dispatch(getManagedListMatchLogs());
    };

    const [headers, setHeader] = useState(createHeader().map(hc =>
        assign(hc, {
            sortOrder: null,
            handleSort: value => handleSort(value),
        })));

    return (
        <div>
            <TableWithPaging
                id={'source-table-with-paging'}
                headers={headers}
                rows={managedListMatchLogsState.managedListMatchLogs}
                countOfResults={managedListMatchLogsState.totalRecords}
                resultsPerPage={managedListMatchLogsState.pageSize}
                currentPage={managedListMatchLogsState.currentPage}
                onSelectPage={(page) => onSelectPage(page)}
            />
        </div>
    )
};


ManagedListMatchLogsTable.propTypes = {
    managedListMatchLogs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            description: PropTypes.string,
            type: PropTypes.string
        })
    ),
    handleSearch: PropTypes.func,
};

ManagedListMatchLogsTable.defaultProps = {
    style: {},
    className: '',
    labelStyle: {},
    managedListMatchLogs: []
};

export default ManagedListMatchLogsTable;
